import { useState, useEffect, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { AdminUserResponse, PriceSource, RateQuoteResponse, ShipmentResponse } from '../../api/types';
import {
  adminCreateShipment,
  adminUpdateShipment,
  getRateQuote,
  type ShipmentPayload,
} from '../../api/endpoints/admin';
import { getDistance, type PlaceDetail } from '../../api/endpoints/places';
import {
  calculateHaversineDistanceKm,
  COMMERCIAL_TRUCK_SPEED_KMH,
} from '../../utils/distance';
import { useMutationHook } from '../../hooks/useMutationHook';
import { UserPicker } from '../../components/domain/UserPicker';
import { AddressAutocompleteField } from '../../components/domain/AddressAutocompleteField';
import { DEFAULT_TRUCK_TYPE_ID } from '../../config/truckCatalog';
import { useTruckCatalog } from '../../hooks/useTruckCatalog';
import { DnmRequestFailure } from '../../api/dnmFailure';
import { Button } from '../../components/ui/Button';
import { TruckIcon } from './TruckIcon';
import { RouteStats } from './RouteStats';
import { LaneHint } from '../profit/LaneHint';
import { curr } from '../../utils/price';
import styles from '../../components/domain/FormModal.module.css';
import { DialogHelp } from '../../help/DialogHelp';
import { DatePicker } from '../../components/ui/DatePicker';
import { DialogShell } from '../../components/ui/DialogShell';
import { useDialogDismiss, useIsDirty } from '../../components/ui/dialogDismiss';
import { isDemo } from '../../demo/flag';
import { validateReason } from '../ownership/opsUser';
import { RateSourcePill } from './RateSourcePill';
import rateStyles from './RateSource.module.css';

/** Contract #249 section 4: the typed "Other" truck is 2–40 characters after trimming. */
const OTHER_MIN = 2;
const OTHER_MAX = 40;
const OTHER_INVALID = 'Describe the truck in 2 to 40 letters, for example 14 ft canter.';
/** Server refusals about the truck choice, shown in the form next to the input (it is kept). */
const TRUCK_REFUSALS = new Set(['TRUCK_TYPE_CONFLICT', 'TRUCK_TYPE_OTHER_INVALID', 'TRUCK_TYPE_UNKNOWN']);

/** Combines the stored address+city into the field's display text. */
function combineAddress(address: string, city: string): string {
  if (!address) return city;
  if (!city || address.includes(city)) return address;
  return `${address}, ${city}`;
}

function getTodayDateString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

interface Props {
  /** When set, the modal edits this shipment (must be POSTED) */
  shipment?: ShipmentResponse | null;
  onClose: () => void;
  onSaved?: (shipment: ShipmentResponse) => void;
}


/**
 * New / Edit shipment modal (design: 480px panel). Creation requires picking
 * the shipper the shipment is created on behalf of.
 */
export function ShipmentFormModal({ shipment, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const isEdit = !!shipment;

  const [shipper, setShipper] = useState<AdminUserResponse | null>(null);

  // Single Places-powered field each for origin/destination (mirrors the
  // mobile shipper app). The initial display text combines the stored
  // address+city; if the admin doesn't touch the field or pick a new
  // suggestion, the original address/city are resubmitted unchanged rather
  // than being collapsed into the combined display text.
  const initialOriginText = shipment ? combineAddress(shipment.originAddress, shipment.originCity) : '';
  const initialDestinationText = shipment
    ? combineAddress(shipment.destinationAddress, shipment.destinationCity)
    : '';
  const [originText, setOriginText] = useState(initialOriginText);
  const [originPlace, setOriginPlace] = useState<PlaceDetail | null>(null);
  const [destinationText, setDestinationText] = useState(initialDestinationText);
  const [destinationPlace, setDestinationPlace] = useState<PlaceDetail | null>(null);

  const [cargoDescription, setCargoDescription] = useState(shipment?.cargoDescription ?? '');
  const [distanceKm, setDistanceKm] = useState<number | null>(shipment?.distanceKm ?? null);
  const [durationMinutes, setDurationMinutes] = useState<number | null>(
    shipment?.estimatedDurationMinutes ?? null,
  );
  const [distanceIsEstimated, setDistanceIsEstimated] = useState<boolean | null>(
    shipment?.distanceIsEstimated ?? null,
  );
  const [isLoadingDistance, setIsLoadingDistance] = useState(false);

  useEffect(() => {
    // If places haven't changed and we already have a usable stored distance for this shipment, keep it
    if (!originPlace && !destinationPlace) {
      if (
        originText === initialOriginText &&
        destinationText === initialDestinationText &&
        shipment?.distanceKm != null
      ) {
        return;
      }
    }

    const origLat = originPlace?.lat ?? (originText === initialOriginText ? shipment?.pickupAddress?.latitude : null);
    const origLng = originPlace?.lng ?? (originText === initialOriginText ? shipment?.pickupAddress?.longitude : null);
    const destLat = destinationPlace?.lat ?? (destinationText === initialDestinationText ? shipment?.dropAddress?.latitude : null);
    const destLng = destinationPlace?.lng ?? (destinationText === initialDestinationText ? shipment?.dropAddress?.longitude : null);

    if (origLat != null && origLng != null && destLat != null && destLng != null) {
      const haversineKm = calculateHaversineDistanceKm(origLat, origLng, destLat, destLng);
      if (haversineKm != null) {
        const estMinutes = Math.round((haversineKm / COMMERCIAL_TRUCK_SPEED_KMH) * 60);
        setDistanceKm(haversineKm);
        setDurationMinutes(estMinutes);
        setDistanceIsEstimated(true);
      }

      let cancelled = false;
      setIsLoadingDistance(true);
      getDistance(origLat, origLng, destLat, destLng)
        .then((res) => {
          if (!cancelled) {
            setDistanceKm(res.distanceKm);
            setDurationMinutes(res.estimatedDurationMinutes);
            setDistanceIsEstimated(res.isEstimated);
          }
        })
        .catch(() => {
          // Keep Haversine fallback on error
        })
        .finally(() => {
          if (!cancelled) {
            setIsLoadingDistance(false);
          }
        });

      return () => {
        cancelled = true;
      };
    } else {
      setDistanceKm(null);
      setDurationMinutes(null);
      setDistanceIsEstimated(null);
    }
  }, [originPlace, destinationPlace, originText, destinationText, initialOriginText, initialDestinationText, shipment]);
  // Weight entered in tons by default (kg togglable); pre-filled to 10 t on create.
  const [weightUnit, setWeightUnit] = useState<'t' | 'kg'>('t');
  const [weight, setWeight] = useState(
    isEdit ? (shipment?.weightKg != null ? (shipment.weightKg / 1000).toString() : '') : '10',
  );
  // Pickers list the active catalog from the API (bundled copy only as fallback, #249).
  const catalog = useTruckCatalog();
  const initialOther = isEdit ? (shipment?.vehicleTypeOther ?? '').trim() : '';
  const initialTruckId = isEdit && !initialOther ? (shipment?.vehicleTypeRequired ?? '') : '';
  // Standardized truck-catalog id; pre-selected to 32 ft Container (MXL) on create.
  const [vehicleTypeRequired, setVehicleTypeRequired] = useState(
    isEdit ? initialTruckId : DEFAULT_TRUCK_TYPE_ID,
  );
  // "Other" (R12): a last-resort typed description, mutually exclusive with a catalog id.
  const [otherSelected, setOtherSelected] = useState(initialOther !== '');
  const [otherText, setOtherText] = useState(initialOther);
  // Only the first category is listed initially so the modal stays short;
  // start expanded when editing a shipment whose truck lives further down.
  const [showAllTrucks, setShowAllTrucks] = useState(() =>
    catalog.categories.slice(1).some((c) => c.trucks.some((t) => t.id === initialTruckId)),
  );
  // An edited shipment whose type is no longer offered (hidden, or a legacy value): shown as a
  // selected "Current" chip and kept unless the user picks something else.
  const keptTruckId = initialTruckId && !catalog.find(initialTruckId) ? initialTruckId : '';
  const keptTruckName = (shipment?.vehicleTypeName ?? '').trim() || keptTruckId;
  const pickTruck = (id: string) => {
    setOtherSelected(false);
    setVehicleTypeRequired(id);
  };
  const toggleOther = () => {
    setOtherSelected((on) => !on);
    setVehicleTypeRequired('');
  };
  const [volumeCbm, setVolumeCbm] = useState(shipment?.volumeCbm?.toString() ?? '');
  const [pickupDate, setPickupDate] = useState(
    isEdit ? (shipment?.pickupDate ?? '') : getTodayDateString(),
  );
  const [offeredPrice, setOfferedPrice] = useState(shipment?.offeredPrice?.toString() ?? '');
  const [error, setError] = useState<string | null>(null);

  // #298 (Contract dnm-work#155, R41/R42): on create, in the demo world only until the build issue, the price
  // pre-fills from the shipper's rate card or the lane default. Changing it is free but needs a reason.
  const quoteEnabled = !isEdit && isDemo();
  const quoteParams =
    quoteEnabled && shipper && originPlace?.state && destinationPlace?.state && vehicleTypeRequired && !otherSelected
      ? {
          shipperId: shipper.id,
          fromState: originPlace.state,
          toState: destinationPlace.state,
          truckType: vehicleTypeRequired,
          ...(cargoDescription.trim() ? { material: cargoDescription.trim() } : {}),
        }
      : null;
  const quoteQuery = useQuery({
    queryKey: ['admin', 'rate-quote', quoteParams],
    queryFn: () => getRateQuote(quoteParams!),
    enabled: quoteParams != null,
  });
  // The quote always belongs to the lane on screen: no lane to quote (e.g. truck "Other") or a quote still
  // loading means no quote, so a stale one can never set the rate source (reviewer #316 finding 2).
  const quote: RateQuoteResponse | null = quoteParams != null && quoteQuery.isSuccess ? quoteQuery.data : null;
  // Any price the user types is theirs: a later quote never replaces it (reviewer #316 finding 1).
  const [priceEdited, setPriceEdited] = useState(false);
  const [priceReason, setPriceReason] = useState('');
  // An untouched pre-filled price always follows the lane on screen: while the new lane's quote loads there is
  // no quote, so the old lane's price is cleared rather than sent as a reasonless MANUAL price (reviewer #316 round 2).
  useEffect(() => {
    if (!quoteEnabled || priceEdited) return;
    setOfferedPrice(quote?.priceRupees != null ? String(quote.priceRupees) : '');
  }, [quoteEnabled, priceEdited, quote]);
  const quotedPrice = quote && quote.source !== 'NONE' ? quote.priceRupees : null;
  const priceSource: PriceSource | null = !quoteEnabled || !offeredPrice
    ? null
    : quotedPrice != null && Number(offeredPrice) === quotedPrice
      ? (quote!.source as PriceSource)
      : 'MANUAL';
  const reasonNeeded = priceSource === 'MANUAL' && quotedPrice != null;
  const changePrice = (value: string) => {
    setOfferedPrice(value);
    setPriceEdited(true);
  };

  const weightAsKg = (): number | undefined => {
    if (!weight) return undefined;
    const parsed = Number(weight);
    if (Number.isNaN(parsed)) return undefined;
    return weightUnit === 't' ? parsed * 1000 : parsed;
  };

  /**
   * #249 Contract section 4: a catalog id or the typed Other, never both. On edit, "" clears the
   * field the user switched away from; an unchanged kept (hidden / legacy) id is not resent.
   */
  const truckPayload = (): Pick<ShipmentPayload, 'vehicleTypeRequired' | 'vehicleTypeOther'> => {
    if (otherSelected) {
      return { vehicleTypeOther: otherText.trim(), ...(isEdit ? { vehicleTypeRequired: '' } : {}) };
    }
    const clearOther = isEdit && initialOther ? { vehicleTypeOther: '' } : {};
    if (keptTruckId && vehicleTypeRequired === keptTruckId) return clearOther;
    return { vehicleTypeRequired: vehicleTypeRequired || undefined, ...clearOther };
  };

  const buildPayload = (): ShipmentPayload => {
    const origin = originPlace
      ? {
          originAddress: originText.trim(),
          originCity: originPlace.city,
          originDistrict: originPlace.district,
          originState: originPlace.state,
          originPincode: originPlace.pincode,
          originLat: originPlace.lat,
          originLng: originPlace.lng,
          originPlaceId: originPlace.placeId,
        }
      : originText !== initialOriginText
        ? { originAddress: originText.trim(), originCity: originText.trim() }
        : { originAddress: shipment?.originAddress ?? originText.trim(), originCity: shipment?.originCity ?? originText.trim() };

    const destination = destinationPlace
      ? {
          destinationAddress: destinationText.trim(),
          destinationCity: destinationPlace.city,
          destinationDistrict: destinationPlace.district,
          destinationState: destinationPlace.state,
          destinationPincode: destinationPlace.pincode,
          destinationLat: destinationPlace.lat,
          destinationLng: destinationPlace.lng,
          destinationPlaceId: destinationPlace.placeId,
        }
      : destinationText !== initialDestinationText
        ? { destinationAddress: destinationText.trim(), destinationCity: destinationText.trim() }
        : {
            destinationAddress: shipment?.destinationAddress ?? destinationText.trim(),
            destinationCity: shipment?.destinationCity ?? destinationText.trim(),
          };

    return {
      ...origin,
      ...destination,
      cargoDescription: cargoDescription.trim() || 'Other',
      weightKg: weightAsKg(),
      ...truckPayload(),
      volumeCbm: volumeCbm ? Number(volumeCbm) : undefined,
      pickupDate,
      offeredPrice: offeredPrice ? Number(offeredPrice) : undefined,
      distanceKm: distanceKm ?? (isEdit ? null : undefined),
      estimatedDurationMinutes: durationMinutes ?? (isEdit ? null : undefined),
      // Sent alongside distanceKm so the server knows whether it is an estimate.
      distanceIsEstimated: distanceKm != null ? distanceIsEstimated : isEdit ? null : undefined,
      ...(priceSource ? { priceSource, priceOverrideReason: reasonNeeded ? priceReason.trim() : null } : {}),
    };
  };

  const saveMut = useMutationHook(
    () =>
      isEdit
        ? adminUpdateShipment(shipment!.id, buildPayload())
        : adminCreateShipment(shipper!.id, buildPayload()),
    {
      successMessage: isEdit
        ? 'Shipment updated.'
        : 'Shipment created and posted to the marketplace.',
      invalidateKeys: [
        ['admin', 'shipments'],
        ['admin', 'dashboard'],
      ],
      onSuccess: (saved) => {
        onSaved?.(saved);
        onClose();
      },
      onError: (err) => {
        if (err instanceof DnmRequestFailure && err.code && TRUCK_REFUSALS.has(err.code)) setError(err.message);
      },
    },
  );

  const validate = (): string | null => {
    if (!isEdit && !shipper) return 'Please select the shipper this shipment belongs to.';
    if (!originText.trim()) return 'Origin is required.';
    if (!destinationText.trim()) return 'Destination is required.';
    if (!pickupDate) return 'Pickup date is required.';
    if (weight && Number(weight) <= 0) return 'Weight must be greater than zero.';
    const other = otherText.trim();
    if (otherSelected && (other.length < OTHER_MIN || other.length > OTHER_MAX)) return OTHER_INVALID;
    if (!weight && !vehicleTypeRequired && !(otherSelected && other))
      return 'Provide at least one of weight or truck type.';
    if (offeredPrice && Number(offeredPrice) <= 0)
      return 'Offered price must be greater than zero.';
    if (reasonNeeded) return validateReason(priceReason);
    return null;
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    saveMut.mutate(undefined);
  };

  const title = isEdit ? `Edit shipment #${shipment!.id}` : 'New shipment';
  const dirty = useIsDirty({
    shipperId: shipper?.id ?? null,
    originText,
    destinationText,
    cargoDescription,
    weightUnit,
    weight,
    vehicleTypeRequired,
    otherSelected,
    otherText,
    volumeCbm,
    pickupDate,
    offeredPrice,
    priceReason,
  });
  const dismiss = useDialogDismiss({ dirty, busy: saveMut.isLoading, onClose });

  return (
    <DialogShell dismiss={dismiss} label={title} scrimClassName={styles.scrim} panelClassName={styles.panel}>
      <div className={styles.titleRow}>
        <h2 className={styles.title}>{title}</h2>
        <DialogHelp context={isEdit ? 'edit-shipment' : 'new-shipment'} title={title} />
      </div>

      <form onSubmit={submit}>
        {!isEdit && (
          <>
            <label className={styles.label}>{t('shipments.shipmentFormModal.shipperLabel')}</label>
            <UserPicker role="SHIPPER" value={shipper} onChange={setShipper} />
          </>
        )}

        <AddressAutocompleteField
          label={t('shipments.shipmentFormModal.originLabel')}
          pinTone="origin"
          placeholder={t('shipments.shipmentFormModal.originPlaceholder')}
          value={originText}
          onChange={setOriginText}
          onPlaceSelected={setOriginPlace}
          onCleared={() => setOriginPlace(null)}
          required
        />

        <AddressAutocompleteField
          label={t('shipments.shipmentFormModal.destinationLabel')}
          placeholder={t('shipments.shipmentFormModal.destinationPlaceholder')}
          value={destinationText}
          onChange={setDestinationText}
          onPlaceSelected={setDestinationPlace}
          onCleared={() => setDestinationPlace(null)}
          required
        />

        <RouteStats
          distanceKm={distanceKm}
          durationMinutes={durationMinutes}
          isRefining={isLoadingDistance}
          isEstimated={distanceIsEstimated === true}
        />

        {/* #300: lane history once both cities are known (demo, managers only; never blocks posting). */}
        <LaneHint
          originCity={originPlace?.city ?? (originText === initialOriginText ? shipment?.originCity : null)}
          destinationCity={
            destinationPlace?.city ?? (destinationText === initialDestinationText ? shipment?.destinationCity : null)
          }
        />

        <label className={styles.label}>{t('shipments.shipmentFormModal.cargoLabel')}</label>
        <input
          className={styles.input}
          value={cargoDescription}
          onChange={(e) => setCargoDescription(e.target.value)}
          placeholder={t('shipments.shipmentFormModal.cargoPlaceholder')}
        />

        <div className={styles.row}>
          <div>
            <label className={styles.label}>{t('shipments.shipmentFormModal.weightLabel')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className={styles.input}
                type="number"
                min="0"
                step="0.01"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={weightUnit === 't' ? '10' : '10000'}
              />
              <div className={styles.unitToggle} role="group" aria-label={t('shipments.shipmentFormModal.weightUnitLabel')}>
                {(['t', 'kg'] as const).map((unit) => (
                  <button
                    key={unit}
                    type="button"
                    className={`${styles.unitBtn} ${weightUnit === unit ? styles.unitBtnActive : ''}`}
                    aria-pressed={weightUnit === unit}
                    onClick={() => setWeightUnit(unit)}
                  >
                    {unit}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div>
            <label className={styles.label}>{t('shipments.shipmentFormModal.volumeLabel')}</label>
            <input
              className={styles.input}
              type="number"
              min="0"
              step="0.1"
              value={volumeCbm}
              onChange={(e) => setVolumeCbm(e.target.value)}
              placeholder="30"
            />
          </div>
        </div>

        <label className={styles.label}>
          {t('shipments.shipmentFormModal.truckTypeLabel')}
        </label>
        {keptTruckId && (
          <div className={styles.truckGrid}>
            <button
              type="button"
              className={`${styles.truckCard} ${vehicleTypeRequired === keptTruckId ? styles.truckCardSelected : ''}`}
              aria-pressed={vehicleTypeRequired === keptTruckId}
              onClick={() => pickTruck(vehicleTypeRequired === keptTruckId ? '' : keptTruckId)}
            >
              <span className={styles.truckName}>{t('shipments.shipmentFormModal.currentTruck', { name: keptTruckName })}</span>
            </button>
          </div>
        )}
        {(showAllTrucks ? catalog.categories : catalog.categories.slice(0, 1)).map((category) => (
          <div key={category.id}>
            <div className={styles.truckCategory}>{category.name}</div>
            <div className={styles.truckGrid}>
              {category.trucks.map((truck) => {
                const isSelected = vehicleTypeRequired === truck.id;
                return (
                  <button
                    key={truck.id}
                    type="button"
                    className={`${styles.truckCard} ${isSelected ? styles.truckCardSelected : ''}`}
                    aria-pressed={isSelected}
                    title={truck.idealFor ?? undefined}
                    onClick={() => pickTruck(isSelected ? '' : truck.id)}
                  >
                    <TruckIcon categoryId={category.id} className={styles.truckIcon} />
                    <span className={styles.truckName}>{truck.name}</span>
                    {truck.capacityTons && <span className={styles.truckMeta}>{t('shipments.shipmentFormModal.capacityTons', { tons: truck.capacityTons })}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <button
          type="button"
          className={styles.truckMore}
          onClick={() => setShowAllTrucks((v) => !v)}
        >
          {showAllTrucks
            ? '▴ Show fewer truck types'
            : `▾ More truck types (${catalog.categories.slice(1).reduce((n, c) => n + c.trucks.length, 0)})`}
        </button>

        {/* "Other" is the last resort (R12, owner dnm-work#114): last, below every category, low-emphasis. */}
        <div className={styles.truckOther}>
          <Button variant="ghost" size="sm" aria-pressed={otherSelected} onClick={toggleOther}>
            {t('shipments.shipmentFormModal.other')}
          </Button>
        </div>
        {otherSelected && (
          <>
            <label className={styles.label} htmlFor="shipment-truck-other">{t('shipments.shipmentFormModal.otherLabel')}</label>
            <input
              id="shipment-truck-other"
              className={styles.input}
              value={otherText}
              maxLength={OTHER_MAX}
              onChange={(e) => setOtherText(e.target.value)}
              placeholder={t('shipments.shipmentFormModal.otherPlaceholder')}
            />
          </>
        )}

        <label className={styles.label} htmlFor="shipment-pickup-date">{t('shipments.shipmentFormModal.pickupDateLabel')}</label>
        <DatePicker
          id="shipment-pickup-date"
          className={styles.input}
          value={pickupDate}
          onChange={setPickupDate}
        />

        <label className={styles.label} htmlFor="shipment-offered-price">{t('shipments.shipmentFormModal.offeredPriceLabel')}</label>
        <input
          id="shipment-offered-price"
          className={styles.input}
          type="number"
          min="0"
          value={offeredPrice}
          onChange={(e) => changePrice(e.target.value)}
          placeholder="55000"
        />
        {priceSource && (priceSource !== 'MANUAL' || quotedPrice != null) && (
          <div className={rateStyles.formRow}>
            <RateSourcePill source={priceSource} />
            {reasonNeeded && <span className={rateStyles.hint}>{t('shipments.shipmentFormModal.quotedPrice', { price: curr(quotedPrice) })}</span>}
          </div>
        )}
        {reasonNeeded && (
          <>
            <label className={styles.label} htmlFor="shipment-price-reason">{t('shipments.shipmentFormModal.priceReasonLabel')}</label>
            <input
              id="shipment-price-reason"
              className={styles.input}
              value={priceReason}
              maxLength={500}
              onChange={(e) => setPriceReason(e.target.value)}
              placeholder={t('shipments.shipmentFormModal.priceReasonPlaceholder')}
            />
          </>
        )}

        {error && <div className={styles.error}>{error}</div>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancel} onClick={dismiss.requestClose}>
            {t('common.actions.cancel')}
          </button>
          <button type="submit" className={styles.submit} disabled={saveMut.isLoading}>
            {saveMut.isLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create shipment'}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}
